import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiEdit, FiTrash2, FiArrowLeft, FiMapPin, FiPhone, FiMail, FiCalendar,
  FiUser, FiUsers, FiAward, FiDollarSign, FiClock, FiBriefcase
} from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { confirmDelete } from '../utils/swal';

const TrainerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [trainer, setTrainer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrainerDetails();
  }, [id]);

  const fetchTrainerDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trainers/${id}`);
      setTrainer(response.data.data);
    } catch (error) {
      console.error('Error fetching trainer details:', error);
      toast.error('Failed to load trainer details');
      navigate('/trainers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!trainer) return;

    const trainerName = `${trainer.first_name} ${trainer.last_name}`;
    const confirmed = await confirmDelete(trainerName, 'trainer');
    if (!confirmed) return;

    try {
      await api.delete(`/trainers/${id}`);
      toast.success('Trainer deleted successfully');
      navigate('/trainers');
    } catch (error) {
      console.error('Error deleting trainer:', error);
      toast.error(error.response?.data?.message || 'Failed to delete trainer');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!trainer) {
    return null;
  }

  // Safely parse certifications
  let certifications = [];
  try {
    if (trainer.certifications) {
      const parsed = typeof trainer.certifications === 'string'
        ? JSON.parse(trainer.certifications)
        : trainer.certifications;
      certifications = Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error('Failed to parse certifications:', error);
    certifications = [];
  }

  // Safely parse schedule
  let schedule = {};
  try {
    if (trainer.availability_schedule) {
      schedule = typeof trainer.availability_schedule === 'string'
        ? JSON.parse(trainer.availability_schedule)
        : trainer.availability_schedule;
    }
  } catch (error) {
    console.error('Failed to parse availability schedule:', error);
    schedule = {};
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/trainers')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <FiArrowLeft /> Back to Trainers
        </button>

        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            {/* Photo */}
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200">
              {trainer.photo ? (
                <img src={trainer.photo} alt={`${trainer.first_name} ${trainer.last_name}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-gray-400">
                  {trainer.first_name?.charAt(0)}{trainer.last_name?.charAt(0)}
                </div>
              )}
            </div>

            {/* Name & Status */}
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-gray-800">
                  {trainer.first_name} {trainer.last_name}
                </h1>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    trainer.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {trainer.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-gray-600">Employee ID: {trainer.employee_id}</p>
              {trainer.specialization && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <FiAward size={14} /> {trainer.specialization}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/trainers/edit/${id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiTrash2 /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h2>
            <div className="grid grid-cols-2 gap-4">
              {trainer.date_of_birth && (
                <div>
                  <p className="text-sm text-gray-500">Date of Birth</p>
                  <p className="text-gray-800">{formatDate(trainer.date_of_birth)}</p>
                </div>
              )}
              {trainer.gender && (
                <div>
                  <p className="text-sm text-gray-500">Gender</p>
                  <p className="text-gray-800 capitalize">{trainer.gender}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-gray-800">{trainer.phone}</p>
              </div>
              {trainer.email && (
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-gray-800">{trainer.email}</p>
                </div>
              )}
            </div>

            {/* Address */}
            {(trainer.address || trainer.city || trainer.state) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-1">Address</p>
                <p className="text-gray-800">
                  {trainer.address}
                  {trainer.city && <>, {trainer.city}</>}
                  {trainer.state && <>, {trainer.state}</>}
                  {trainer.pincode && <> - {trainer.pincode}</>}
                </p>
              </div>
            )}
          </div>

          {/* Professional Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Professional Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Branch</p>
                <p className="text-gray-800">{trainer.branch_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Joining Date</p>
                <p className="text-gray-800">{formatDate(trainer.joining_date)}</p>
              </div>
              {trainer.experience_years && (
                <div>
                  <p className="text-sm text-gray-500">Experience</p>
                  <p className="text-gray-800">{trainer.experience_years} years</p>
                </div>
              )}
              {trainer.salary && (
                <div>
                  <p className="text-sm text-gray-500">Salary</p>
                  <p className="text-gray-800">₹{parseFloat(trainer.salary).toLocaleString()}/month</p>
                </div>
              )}
              {trainer.commission_percent > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Commission</p>
                  <p className="text-gray-800">{trainer.commission_percent}%</p>
                </div>
              )}
            </div>
          </div>

          {/* Certifications */}
          {certifications.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FiAward /> Certifications
              </h2>
              <div className="space-y-3">
                {certifications.map((cert, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-800">{cert.name}</p>
                    <p className="text-sm text-gray-500">Year: {cert.year}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Availability Schedule */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FiClock /> Availability Schedule
            </h2>
            <div className="space-y-2">
              {Object.keys(schedule).map(day => (
                <div key={day} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium capitalize text-gray-800">{day}</span>
                  {schedule[day].available ? (
                    <span className="text-gray-600">
                      {formatTime(schedule[day].start)} - {formatTime(schedule[day].end)}
                    </span>
                  ) : (
                    <span className="text-red-600">Not Available</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Assigned Members */}
          {trainer.assigned_members && trainer.assigned_members.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FiUsers /> Assigned Members ({trainer.assigned_members.length})
              </h2>
              <div className="space-y-2">
                {trainer.assigned_members.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-semibold text-gray-800">
                        {member.first_name} {member.last_name}
                      </p>
                      <p className="text-sm text-gray-500">ID: {member.member_id}</p>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>{member.phone}</p>
                      {member.email && <p className="text-xs">{member.email}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Stats</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FiUsers className="text-blue-600" size={24} />
                  <div>
                    <p className="text-sm text-gray-600">Assigned Members</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {trainer.assigned_members?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FiAward className="text-purple-600" size={24} />
                  <div>
                    <p className="text-sm text-gray-600">Certifications</p>
                    <p className="text-2xl font-bold text-gray-800">{certifications.length}</p>
                  </div>
                </div>
              </div>

              {trainer.experience_years && (
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FiBriefcase className="text-green-600" size={24} />
                    <div>
                      <p className="text-sm text-gray-600">Experience</p>
                      <p className="text-2xl font-bold text-gray-800">{trainer.experience_years} yrs</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate(`/attendance/trainer/${id}`)}
                className="w-full text-left px-4 py-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              >
                View Attendance
              </button>
              <button
                onClick={() => navigate(`/salary/trainer/${id}`)}
                className="w-full text-left px-4 py-3 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
              >
                Salary Records
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Metadata</h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <FiCalendar className="flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p>{formatDate(trainer.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <FiCalendar className="flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Last Updated</p>
                  <p>{formatDate(trainer.updated_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainerDetail;
